import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const KEY = "hg_exit_intent_shown";
const MOBILE_INACTIVITY_MS = 30000;

// Suppress for the CURRENT browsing session only (per requirements): once the
// visitor has seen/dismissed it, don't show again this session — but a fresh
// session will show it again. Uses sessionStorage so it never gets "stuck" for
// days the way a long-lived localStorage flag did.
const alreadyShownThisSession = () => {
  try {
    return sessionStorage.getItem(KEY) === "1";
  } catch (e) {
    return false;
  }
};

const markShownThisSession = () => {
  try { sessionStorage.setItem(KEY, "1"); } catch (e) { /* ignore */ }
};

const isLoggedIn = () => {
  try { return !!localStorage.getItem("hg_token"); } catch (e) { return false; }
};

export const ExitIntentModal = () => {
  const [open, setOpen] = useState(false);

  const trigger = useCallback(() => {
    if (open || alreadyShownThisSession() || isLoggedIn()) return;
    setOpen(true);
    markShownThisSession();
  }, [open]);

  useEffect(() => {
    if (alreadyShownThisSession() || isLoggedIn()) return;

    // Desktop: mouse leaves the top of the viewport.
    const onMouseOut = (e) => {
      if (!e.relatedTarget && e.clientY <= 0) trigger();
    };

    // Mobile / touch: long inactivity, or when the tab is being hidden/left.
    const isTouch = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(pointer: coarse)").matches;
    let inactivityTimer;
    const resetInactivity = () => {
      clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(trigger, MOBILE_INACTIVITY_MS);
    };
    const onVisibility = () => { if (document.visibilityState === "hidden") trigger(); };

    document.addEventListener("mouseout", onMouseOut);
    if (isTouch) {
      ["touchstart", "scroll", "click"].forEach((ev) => window.addEventListener(ev, resetInactivity, { passive: true }));
      window.addEventListener("popstate", trigger);
      document.addEventListener("visibilitychange", onVisibility);
      resetInactivity();
    }

    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      clearTimeout(inactivityTimer);
      ["touchstart", "scroll", "click"].forEach((ev) => window.removeEventListener(ev, resetInactivity));
      window.removeEventListener("popstate", trigger);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [trigger]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md" data-testid="exit-intent-modal">
        <DialogHeader>
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <ShieldCheck className="h-5 w-5" />
          </div>
          <DialogTitle className="font-display text-xl">Wait! Protect your next booking.</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            Create your free HostGuard account in less than 30 seconds and start assessing bookings before you accept them.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-2 flex flex-col gap-2 sm:flex-col">
          <Link to="/signup" className="w-full" onClick={() => setOpen(false)}>
            <Button className="w-full gap-2" data-testid="exit-intent-signup">Create Free Account <ArrowRight className="h-4 w-4" /></Button>
          </Link>
          <Button variant="ghost" className="w-full text-muted-foreground" onClick={() => setOpen(false)} data-testid="exit-intent-dismiss">
            Continue Browsing
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
